import type {CompetitiveCommitConfig} from '@/services/OMService';

/**
 * Per-type config for the staging pipeline, mirroring `omConfig`. Differs from OM:
 * dedicated `-bulk` collections, FLAT (dotless) marker icons, no Salesforce.
 */
interface BulkConfigItem extends CompetitiveCommitConfig {
  /** Staging collection this record type lives in until approved. */
  stagingCollection: string;
  /** Staging-doc field holding the committed doc's id after approval. */
  finalIdKey: 'createdCompetitiveBuildingId' | 'createdCompetitivePortfolioId';
  // Narrowed from base `string` for bulk's fixed collections.
  finalFirestoreCollection: 'beyondMaps-properties' | 'beyondMaps-portfolios';
  finalFilterCollectionType: 'competitiveBuilding' | 'competitivePortfolio';
  /** Committed filter collection (getCollection key) this record type lands in. */
  finalFilterCollection: 'competitiveBuildings' | 'competitivePortfolios';
  /** Root store array holding this type's staged records. */
  stateKey: 'bulkCompetitiveBuildings' | 'bulkCompetitivePortfolios';
  type: 'building' | 'portfolio';
  /** Staging collection's changelog table; its rows are copied to the final table at commit. */
  bigQuerySourceTable: string;
  bigQueryDestTable: string;
}

const BUILDING_STAGING_COLLECTION = 'beyondMaps-properties-bulk';
const PORTFOLIO_STAGING_COLLECTION = 'beyondMaps-portfolios-bulk';

const bulkConfig: Record<'building' | 'portfolio', BulkConfigItem> = {
  building: {
    stagingCollection: BUILDING_STAGING_COLLECTION,
    finalFirestoreCollection: 'beyondMaps-properties',
    finalFilterCollectionType: 'competitiveBuilding',
    finalInfoWindowAction: 'createCompetitiveBuildingTab',
    finalIdKey: 'createdCompetitiveBuildingId',
    finalFilterCollection: 'competitiveBuildings',
    stateKey: 'bulkCompetitiveBuildings',
    fullType: 'bulkCompetitiveBuilding',
    localCollection: 'bulkCompetitiveBuildings',
    markerIcon: 'Blue-Balloon-Flat',
    type: 'building',
    bigQuerySourceTable: 'propertiesbulk_export_raw_changelog',
    bigQueryDestTable: 'properties_export_raw_changelog',
  },
  portfolio: {
    stagingCollection: PORTFOLIO_STAGING_COLLECTION,
    finalFirestoreCollection: 'beyondMaps-portfolios',
    finalFilterCollectionType: 'competitivePortfolio',
    finalInfoWindowAction: 'createCompetitivePortfolioTab',
    finalIdKey: 'createdCompetitivePortfolioId',
    finalFilterCollection: 'competitivePortfolios',
    stateKey: 'bulkCompetitivePortfolios',
    fullType: 'bulkCompetitivePortfolio',
    localCollection: 'bulkCompetitivePortfolios',
    markerIcon: 'Blue-Portfolio-Flat',
    type: 'portfolio',
    bigQuerySourceTable: 'portfoliosbulk_export_raw_changelog',
    bigQueryDestTable: 'portfolios_export_raw_changelog',
  },
};

export type {BulkConfigItem};
export {bulkConfig, BUILDING_STAGING_COLLECTION, PORTFOLIO_STAGING_COLLECTION};
